import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon, Tooltip, useTheme } from '@Sage/hoi-poi-ui';
import CopilotLauncher from 'components/CopilotLauncher';
import Tabs from 'components/Tabs';
import { getLiteral } from 'utils/getLiteral';

const propTypes = {
    onClickSeeAll: PropTypes.func,
    onClickAdd: PropTypes.func,
    content: PropTypes.array,
    onSelectTab: PropTypes.func,
    tabControlled: PropTypes.number,
    tabsHeaderClass: PropTypes.string,
    tabsClassesObj: PropTypes.object,
    tabClassesObj: PropTypes.object,
    classNameForTabs: PropTypes.string,
    isWidget: PropTypes.bool,
    withScroll: PropTypes.bool,
    showScrollButtons: PropTypes.bool,
};

const WidgetAddIcon = ({ onClick }) => {
    const [isHovered, setIsHovered] = useState(false);
    const theme = useTheme();

    return (
        <Icon
            name="addCircle"
            size="large"
            color={isHovered ? theme.colors.actionMajor[600] : theme.colors.actionMajor[500]}
            onClick={onClick || null}
            onMouseOver={() => setIsHovered(true)}
            onMouseOut={() => setIsHovered(false)}
        />
    );
};

const WidgetHeaderLayout = (props) => {
    const theme = useTheme();
    const {
        content,
        children,
        className,
        permissions = { create: true },
        onSelectTab,
        onClickSeeAll,
        onClickAdd,
        buttonAdd,
        tabControlled,
        tabsHeaderClass,
        tabsClassesObj,
        tabClassesObj,
        withScroll,
        showScrollButtons,
        classNameForTabs,
        leftPreComponent,
        rightPostComponent,
        rightPostComponentWithoutDivider,
        tooltipLiteral,
        copilotLauncherConfig,
    } = props;

    let classes = ['fm-widget-header-layout'];

    if (className) classes.push(className);
    if (content && content.length === 1) classes.push('fm-widget-header-layout--single-tab');

    const renderLeft = useMemo(
        () => (
            <div className="fm-widget-header-layout__left">
                {leftPreComponent && (
                    <div className="fm-widget-header-layout__left-preComponent">
                        {leftPreComponent}
                    </div>
                )}
                <Tabs
                    className={classNameForTabs}
                    content={content}
                    onChange={onSelectTab}
                    tabControlled={tabControlled}
                    tabsHeaderClass={tabsHeaderClass}
                    tabsClassesObj={tabsClassesObj}
                    tabClassesObj={tabClassesObj}
                    withScroll={withScroll}
                    showScrollButtons={showScrollButtons}
                    isWidget={true}
                />
            </div>
        ),
        [
            classNameForTabs,
            content,
            onSelectTab,
            showScrollButtons,
            tabClassesObj,
            tabControlled,
            tabsClassesObj,
            tabsHeaderClass,
            withScroll,
            leftPreComponent,
        ],
    );

    const renderCopilotLauncher = useMemo(() => {
        if (!copilotLauncherConfig) return null;
        return <CopilotLauncher {...copilotLauncherConfig} />;
    }, [copilotLauncherConfig]);

    const renderButtonSeeDetail = useMemo(() => {
        if (!onClickSeeAll) return null;
        return (
            <div className="button-see-detail">
                <Tooltip placement="top" content={getLiteral('action_showmore_widgets')}>
                    <div className="fm-button__view-details-widget" onClick={onClickSeeAll}>
                        <div className="fm-button__view-details-widget__icon">
                            <Icon name="launch" color={theme.colors.actionMinor[500]} />
                        </div>
                    </div>
                </Tooltip>
            </div>
        );
    }, [onClickSeeAll, theme]);

    const renderButtonAdd = useMemo(() => {
        if (!onClickAdd && !buttonAdd) return null;

        return (
            <Tooltip placement="top" content={getLiteral(tooltipLiteral || 'action_add_more')}>
                <div className="fm-widget-header-layout__button-add">
                    {!buttonAdd && <WidgetAddIcon onClick={onClickAdd} />}
                    {buttonAdd && buttonAdd}
                </div>
            </Tooltip>
        );
    }, [buttonAdd, onClickAdd, tooltipLiteral]);

    const renderRight = useMemo(() => {
        return (
            <div className="fm-widget-header-layout__right">
                {renderCopilotLauncher}
                {renderButtonSeeDetail}
                {permissions.create && renderButtonAdd}
                {rightPostComponent && (
                    <div className="post-component">
                        {!rightPostComponentWithoutDivider && <div className="divider" />}
                        {rightPostComponent}
                    </div>
                )}
            </div>
        );
    }, [
        permissions.create,
        renderButtonAdd,
        renderButtonSeeDetail,
        renderCopilotLauncher,
        rightPostComponent,
        rightPostComponentWithoutDivider,
    ]);

    const renderHeader = () => {
        return (
            <>
                <div className="fm-widget-header-layout-content">
                    {renderLeft}
                    {renderRight}
                </div>
            </>
        );
    };

    return <div className={classes.join(' ')}>{children ? children : renderHeader()}</div>;
};

WidgetHeaderLayout.propTypes = propTypes;

export default WidgetHeaderLayout;
